import axios from 'axios';
import * as ReactQuery from 'react-query';
import { ReliningImageNameEnum } from '@trace/types';
import { DocumentRequestBase } from '../../../services/documents';
import { identify, makeRequest, Service } from '../../../utils/http';
import { InferMutationOptions } from '../../../utils/types';

type UploadFileData = DocumentRequestBase & {
  file: File;
};

/*
 * Uploads a single PDF document for a relining entry.
 *
 * Re-uses the existing relining upload-url mechanism (the same one used for
 * images), which stores the object publicly-readable in the documents S3
 * bucket and returns a stable public URL. The PDF is streamed as-is (no
 * image compression) with an `application/pdf` content type.
 *
 * The resulting public URL is returned to the caller, which writes it into
 * the form data so it is persisted on autosave and later embedded as a link
 * in the generated certificate.
 */
async function uploadReliningFileApi({
  file,
  templateId,
  documentId,
}: UploadFileData) {
  const { signedUrl, uploadUrl } = await makeRequest(
    Service.getReliningUploadImageUrl(
      documentId,
      templateId,
      ReliningImageNameEnum.INSTALLATION_DOCUMENT,
      await identify(),
    ),
  );

  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type': 'application/pdf',
    },
  });

  return {
    uploadUrl,
    fileName: file.name,
    timestamp: Date.now(),
  };
}

function useUploadReliningFile(
  options?: InferMutationOptions<typeof uploadReliningFileApi>,
) {
  const { mutate: uploadReliningFile, ...rest } = ReactQuery.useMutation(
    'uploadReliningFile',
    uploadReliningFileApi,
    options,
  );

  return {
    uploadReliningFile,
    ...rest,
  };
}

export { useUploadReliningFile };

